import React, { useMemo } from 'react';
import styles from '../../assets/Onboarding/CompleteProfile.module.scss';
import { Button, Loader, Paragraph, Svg } from '@wunder/ui-components';
import { useTypedSelector } from '@/core/redux/hooks';
import { getMeData } from '@/features/account/redux/account.selectors';
import { useNavigate } from 'react-router';
import { useVerificationStatus } from '@/features/account/hooks/useVerificationStatus';
import { accountChildRoutes, verification } from '@/core/pages/Account/routes';
import { useStepStatus } from '@/features/account/hooks/useStepStatus';
import { Trans } from '@lingui/react/macro';
import { useAccountProfileComplete } from '@/features/account/hooks/useAccountProfileComplete';
import { getStepsStatus } from '../../redux/account.selectors';
import { useIsMapLoaded } from '@/features/geomap/hooks/useIsMapLoaded';

const CompleteProfile = () => {
  const navigate = useNavigate();
  const mapLoaded = useIsMapLoaded();
  const verificationStatus = useVerificationStatus();
  const meData = useTypedSelector(getMeData);
  const accountCompleted = useAccountProfileComplete();
  const stepsStatus = useStepStatus();
  const calculatedWidth = (stepsStatus.completed / stepsStatus.total) * 100;
  const steps = useTypedSelector(getStepsStatus);

  const incompleteStepIndex = useMemo(() => {
    const stepsArray = Object.values(steps);

    return stepsArray.findIndex((step) => step.isComplete === false);
  }, [steps]);

  if (!meData || accountCompleted || !mapLoaded) return null;

  const verificationPending = verificationStatus === 'pending';
  const verificationRejected = verificationStatus === 'rejected';

  return (
    <div
      tabIndex={-1}
      role="button"
      onKeyDown={() => {}}
      onClick={() => navigate(accountChildRoutes[incompleteStepIndex]?.path ?? verification.path)}
      className={styles['complete-profile']}
    >
      <div className={styles['complete-profile__avatar']}>
        <Svg icon="user" color="icon-0" width={32} height={32} />
      </div>
      <div className={styles['complete-profile__content']}>
        <div className={styles['complete-profile__completion']}>
          <div className={styles['complete-profile__completion-info']}>
            <Paragraph marginBottom={0}>
              {verificationPending ? (
                <Trans id="account.completeProfile.drivingLicenceVerification">
                  Your driving license is being verified
                </Trans>
              ) : (
                <Trans id="account.completeProfile.complete">Complete profile</Trans>
              )}
            </Paragraph>
            <Paragraph marginBottom={0} textColor="text-400">
              {stepsStatus.completed}/{stepsStatus.total}
            </Paragraph>
          </div>
          {verificationRejected && (
            <Paragraph size={4} textColor="error">
              <Trans id="account.completeProfile.drivingLicenceVerificationFailed">
                There was a problem verifying your drivers licence.
              </Trans>
            </Paragraph>
          )}
          <div className={styles['complete-profile__completion-rate']}>
            <div
              style={{ width: `${calculatedWidth}%` }}
              className={styles['complete-profile__completion-completed']}
            />
          </div>
        </div>

        {verificationPending ? (
          <Loader width={32} />
        ) : (
          <Button noPadding variation="link" iconName="arrowRight" />
        )}
      </div>
    </div>
  );
};

export default CompleteProfile;
